import { useTranslation } from "react-i18next";
import { Button } from "@foodpilot/foods";
import { CircularProgress, Stack } from "@mui/material";

import { CreatePageDialog } from "./components/CreatePageDialog";
import { DeleteConfirmDialog } from "./components/DeleteConfirmDialog";
import { Description, Home } from "./components/IconsExport";
import { CMSEditorPagesTable } from "./components/CMSEditorPagesTable";
import { CMSLayout } from "../CMSLayout";
import { useCMSPagesManagerController } from "./hooks/useCMSPagesManagerController";
import { CREATE, DELETE, PUCK_PAGE, useFoodpilotContext } from "src/context/FoodpilotContext";

export const CMSPagesManager = () => {
    const { t } = useTranslation();
    const { canUser } = useFoodpilotContext();
    const controller = useCMSPagesManagerController();
    const canCreatePage = canUser(CREATE, PUCK_PAGE);
    const canDeletePage = canUser(DELETE, PUCK_PAGE);

    const topBar =
        canCreatePage ?
            <Stack direction="row" spacing={2}>
                {!controller.homePageExists && (
                    <Button
                        variant="ternary"
                        color="primary"
                        startIcon={<Home />}
                        onClick={controller.handleCreateHomePage}
                        disabled={controller.isCreating}
                    >
                        {controller.isCreating ?
                            <CircularProgress size={24} sx={{ color: "white" }} />
                        :   t("settings.cms.pages.create_homepage", "Create Homepage")}
                    </Button>
                )}
                <Button variant="ternary" startIcon={<Description />} onClick={controller.openCreateDialog}>
                    {t("settings.cms.pages.create", "Create Page")}
                </Button>
            </Stack>
        :   undefined;

    return (
        <CMSLayout
            title={t("settings.cms.pages.title", "Pages")}
            isLoading={controller.isLoading}
            isError={controller.isError}
            topBar={topBar}
        >
            <CMSEditorPagesTable
                pages={controller.pages}
                onEdit={controller.navigateToEditor}
                onDelete={controller.confirmDelete}
                onUpdateTitle={controller.handleUpdateTitle}
                onUpdateRoles={controller.handleUpdatePageRoles}
                onUpdateMeta={controller.handleUpdateMeta}
                onUpdateStatus={controller.handleUpdateStatus}
                onDuplicatePage={controller.handleDuplicatePage}
                pageType="CMS"
                pagination={controller.table.pagination}
                setPagination={controller.table.setPagination}
                sorting={controller.table.sorting}
                setSorting={controller.table.setSorting}
                columnFilters={controller.table.columnFilters}
                setColumnFilters={controller.table.setColumnFilters}
                totalItems={controller.totalItems}
                isServerSide={true}
                isCreating={controller.isCreating}
            />

            {canCreatePage && (
                <CreatePageDialog
                    open={controller.isCreateDialogOpen}
                    onClose={controller.closeCreateDialog}
                    onCreatePage={controller.handleCreatePage}
                    isCreating={controller.isCreating}
                />
            )}

            {canDeletePage && (
                <DeleteConfirmDialog
                    open={controller.deleteConfirmDialogOpen}
                    onClose={controller.closeDeleteConfirmDialog}
                    onConfirmDelete={controller.handleDeletePage}
                    isDeleting={controller.actions.isDeleting}
                    pageName={controller.pageToDelete || ""}
                />
            )}
        </CMSLayout>
    );
};
